import { createContext, useContext, useEffect, useRef } from 'react';

import type { ExcelWorkbookSpec } from '../util/excel-export';

/**
 * Per-section Excel export plumbing.
 *
 * The Export button lives in the section *header* (or the Field page's tab
 * bar) but the data and the user's filters / sort / search live in the
 * section *body*. So the body registers a builder via `useSectionExport`,
 * and the button calls it on click — the export is always exactly what
 * the section is currently showing, every page of it, not just the
 * visible slice.
 *
 * Bodies are unmounted while their section is collapsed (or their tab is
 * inactive), so the button only appears once the body is mounted and its
 * data has loaded. Outside a provider the hook is a no-op.
 */

// May be async — e.g. to fetch a full list the section only shows the top of.
export type Builder = () => ExcelWorkbookSpec | null | Promise<ExcelWorkbookSpec | null>;

interface SectionExportRegistry {
  setBuilder: (builder: Builder | null) => void;
}

export const SectionExportCtx = createContext<SectionExportRegistry | null>(null);
export const SectionExportBuilderCtx = createContext<Builder | null>(null);

/**
 * Register this section's export. `build` is read at click time, so it
 * can close over the latest filtered / sorted rows without re-registering
 * on every render. Pass `enabled = false` while data is loading.
 */
export function useSectionExport(build: Builder, enabled = true): void {
  const ctx = useContext(SectionExportCtx);
  const buildRef = useRef(build);
  buildRef.current = build;

  useEffect(() => {
    if (!ctx || !enabled) return;
    ctx.setBuilder(() => buildRef.current());
    return () => ctx.setBuilder(null);
  }, [ctx, enabled]);
}
